import { LoadingOverlay } from '@mantine/core'
import { DashboardsRoute } from 'App/dashboards/DashboardsRoute'
import { Map } from 'App/Map/Map'
import { IcsDashboard } from 'components/IcsDashboard/IcsDashboard'
import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { smallStore } from 'stores/smallStore/smallStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { apiTokenGetter } from 'utils/apiTokenGetter'
import { getLoginRedirect } from 'utils/getLoginRedirect'
import { pushRoute } from 'utils/pushRoute'
import { Authentication } from './Authentication'
import { ResetPassword } from './Authentication/ResetPassword'
import type { TCurrentTabSelection } from './Map/UserSettingsMenu/types'
import { PrivacyPolicy } from './PrivacyPolicy/PrivacyPolicy'
import { Sula } from './Sula/Sula'
import { Tac } from './Tac/Tac'

// same check as SHOW_UI_EXAMPLES, inline so the bundler can drop the gallery chunk from production builds
const UIExamples =
  import.meta.env.DEV || import.meta.env.VITE_SHOW_UI_EXAMPLES === 'true'
    ? lazy(() => import('./UIExamples/UIExamples').then((m) => ({ default: m.UIExamples })))
    : undefined

const setModalTab = (tab: TCurrentTabSelection) => {
  smallStore.setState((s) => ({
    ...s,
    showSettingsModal: true,
    settingsModalSelectedTab: tab,
  }))
}

const RequireAuthOrPermission = ({
  children,
  checkType,
}: {
  children: React.JSX.Element
  checkType?: 'alert' | 'report' | 'ics-dashboard'
}) => {
  const token = apiTokenGetter()
  const location = useLocation()
  const atAlerts = location.pathname === '/alerts'
  const atReports = location.pathname === '/reports'
  const atIcsDashboard = location.pathname === '/ics-dashboard'
  const permissions = userDetailsStore.useSelector((s) => s.permissions)
  const permissionsReady = permissions && Object.keys(permissions).length > 0
  const userCanCreateAlert = Object.keys(permissions || {}).some((p) => /^CREATE_.*_ALERT$/.test(p))
  const userCanCreateReport = Object.keys(permissions || {}).some((p) => p.startsWith('CREATE_REPORT'))
  const userCanViewIcsDashboard = Object.keys(permissions || {}).some((p) => p === 'VIEW_ICS_DASHBOARD')

  let permissionCheck: boolean | undefined

  if (checkType === 'alert') {
    permissionCheck = userCanCreateAlert
  } else if (checkType === 'report') {
    permissionCheck = userCanCreateReport
  } else if (checkType === 'ics-dashboard') {
    permissionCheck = userCanViewIcsDashboard
  }

  useEffect(() => {
    if (permissionsReady) {
      if (!permissionCheck && (atAlerts || atReports || atIcsDashboard)) {
        pushRoute('/')
      } else {
        if (atAlerts) setModalTab('alerts')

        if (atReports) setModalTab('reports')
      }
    }
  }, [permissionsReady])

  if (!token) {
    const { hasSource, sourceValue, redirectUri } = getLoginRedirect(location)

    if (hasSource) {
      return <Navigate to={`/login?source=${sourceValue}&redirectUri=${redirectUri}`} replace />
    }

    return <Navigate to={`/login?redirectUri=${redirectUri}`} replace />
  }

  if (!permissionsReady && !token) return <LoadingOverlay visible={true} />

  return children
}

export const AppRoutes = () => {
  return (
    <Routes>
      <Route path="privacy/*" element={<PrivacyPolicy />} />
      <Route path="sula/*" element={<Sula />} />
      <Route path="tac/*" element={<Tac />} />
      {UIExamples && (
        <Route
          path="ui-examples/*"
          element={
            <Suspense fallback={null}>
              <UIExamples />
            </Suspense>
          }
        />
      )}
      <Route path="reset/*" element={<ResetPassword />} />
      <Route path="choosepassword/*" element={<ResetPassword />} />
      <Route path="login/*" element={<Authentication />} />
      <Route
        path="alerts"
        element={
          <RequireAuthOrPermission checkType="alert">
            <Map />
          </RequireAuthOrPermission>
        }
      />

      <Route
        path="reports"
        element={
          <RequireAuthOrPermission checkType="report">
            <Map />
          </RequireAuthOrPermission>
        }
      />

      <Route
        path="map"
        element={
          <RequireAuthOrPermission>
            <Map />
          </RequireAuthOrPermission>
        }
      />

      <Route
        path="ics-dashboard"
        element={
          <RequireAuthOrPermission checkType="ics-dashboard">
            <IcsDashboard />
          </RequireAuthOrPermission>
        }
      />

      <Route
        path="dashboards/*"
        element={
          <RequireAuthOrPermission>
            <DashboardsRoute />
          </RequireAuthOrPermission>
        }
      />

      <Route
        path="*"
        element={
          <RequireAuthOrPermission>
            <Map />
          </RequireAuthOrPermission>
        }
      />
    </Routes>
  )
}
